// Booking-engine payload → Kyte ShopFlightsRequest translator (POST
// /api/v3/flights/shop). Symmetric with ./normalize.
// Schema: https://docs.gokyte.com/#schemashopflightsrequest

import { is_infant } from "@liveflight/utilities/travelers";

const CABIN_MAP = {
  ECONOMY: "economy",
  PREMIUM_ECONOMY: "premiumEconomy",
  BUSINESS: "business",
  FIRST: "first",
};

// Adults always report age 30 — Kyte's pricing engine rejects an empty {}
// (hit on lfk_search.php) and 30 is a neutral mid-range adult. Children /
// infants forward the actual age preserved by buildTravelersArray.
const ADULT_AGE = 30;
const build_passenger = (traveler) => {
  const passenger = { age: ADULT_AGE };
  if (traveler && typeof traveler.age === "number") {
    passenger.age = Math.max(0, traveler.age);
  }
  if (is_infant(traveler)) {
    passenger.seatRequested = false;
  }
  return passenger;
};

/**
 * Map every traveler in the occupancy to a Kyte ShopFlightsRequest passenger
 * entry. Exported so consumers (tests, ad-hoc reprice) can build search bodies
 * without going through build_search_request.
 * @param {Array} travelers
 * @returns {Array}
 */
export const build_search_passengers = (travelers) => {
  const list = Array.isArray(travelers) ? travelers : [];
  return list.map(build_passenger);
};

const build_journey = (od) => {
  const {
    id = "",
    originLocationCode = "",
    destinationLocationCode = "",
    departureDateTimeRange: { date = "" } = {},
  } = od || {};
  return {
    id: id ? String(id) : "",
    date: { main: date, type: "departure" },
    departureAirport: originLocationCode,
    arrivalAirport: destinationLocationCode,
    action: "add",
  };
};

const pick_cabin = (search_criteria) => {
  const restrictions =
    search_criteria &&
    search_criteria.flightFilters &&
    search_criteria.flightFilters.cabinRestrictions;
  if (!Array.isArray(restrictions) || restrictions.length === 0) {
    return null;
  }
  const first = restrictions[0] || {};
  return CABIN_MAP[first.cabin] || null;
};

const is_non_stop = (search_criteria) => {
  const cr =
    search_criteria &&
    search_criteria.flightFilters &&
    search_criteria.flightFilters.connectionRestriction;
  if (!cr) {
    return false;
  }
  return Number(cr.maxNumberOfConnections) === 0;
};

/**
 * Translate the generic UI payload to the
 * Kyte ShopFlightsRequest body.
 * @param {object} payload
 * @returns {object}
 */
export const build_search_request = (payload) => {
  const body = payload || {};
  const origin_destinations = Array.isArray(body.originDestinations)
    ? body.originDestinations
    : [];
  const travelers = Array.isArray(body.travelers) ? body.travelers : [];

  const request = {
    journeys: origin_destinations.map(build_journey),
    passengers: build_search_passengers(travelers),
    exactMatch: false,
  };

  const cabin = pick_cabin(body.searchCriteria);
  if (cabin) {
    request.cabinType = cabin;
  }

  if (is_non_stop(body.searchCriteria)) {
    request.nonStopFlight = true;
  }

  return request;
};
